import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';
import { Authority } from '../../../module/authority/model/Authority';
import { Role } from '../../../module/role/model/Role';
import { Product } from '../../../module/product/model/Product';
import { ProfileResponse } from '@module/profile/model/ProfileResponse';

export const useSecurity = () => {
  const authentication = useSelector(
    (store: RootState) => store.session.authentication
  );

  const getProfileId = () => {
    return authentication.profile?.id;
  };

  const isBuyer = () => {
    return (
      authentication && authentication.profile && authentication.profile.isBuyer
    );
  };

  const isAgent = () => {
    return (
      authentication && authentication.profile && authentication.profile.isAgent
    );
  };

  const isVendor = () => {
    return authentication?.profile?.isVendor;
  };

  const isAuthenticated = () => {
    return authentication && authentication.profile;
  };

  const resolveBuyerProfile = (
    buyerCompanyId: string | undefined
  ): ProfileResponse | undefined => {
    const profile = authentication.profile!;
    const isAdminInBuyerMode = profile.isAdmin && profile.isAdminInBuyerMode;

    if (isAdminInBuyerMode && buyerCompanyId) {
      return profile.adminLinkedBuyerProfiles?.find(
        (profile) => profile.companyId === buyerCompanyId
      );
    }
    return profile;
  };

  const hasAnyAuthorityOnBuyersBehalf = (
    hasAnyAuthority: Authority[] | undefined,
    buyerCompanyId: string | undefined
  ) => {
    if (!hasAnyAuthority || !isAuthenticated()) return false;
    const authorities = resolveBuyerProfile(buyerCompanyId)?.authorities;
    if (!authorities || authorities.length == 0) return false;
    return hasAnyAuthority.some((a) =>
      authorities
        .filter((pa) => pa.isEnabled)
        .map((pa) => pa.authority)
        .includes(a)
    );
  };

  const hasAnyAuthority = (hasAnyAuthority: Authority[] | undefined) => {
    if (!hasAnyAuthority || !isAuthenticated()) return false;
    const authorities = authentication.profile!.authorities;
    if (!authorities || authorities.length == 0) return false;

    return hasAnyAuthority.some((a) =>
      authorities
        .filter((pa) => pa.isEnabled)
        .map((pa) => pa.authority)
        .includes(a)
    );
  };

  const isForDepartments = (
    forDepartments: (string | undefined)[] | undefined
  ) => {
    if (!forDepartments || !isAuthenticated()) return false;
    return forDepartments
      .filter((d): d is string => typeof d !== 'undefined')
      .includes(authentication.profile!.department!.name);
  };

  const isForDepartmentsOnBuyersBehalf = (
    forDepartments: (string | undefined)[] | undefined,
    buyerCompanyId: string | undefined
  ) => {
    if (!forDepartments || !isAuthenticated()) return false;
    const buyerProfile = resolveBuyerProfile(buyerCompanyId);
    return (
      buyerProfile &&
      forDepartments
        .filter((d): d is string => typeof d !== 'undefined')
        .includes(buyerProfile.department!.name)
    );
  };

  const isForRole = (forRoles: Role[] | undefined) => {
    if (!forRoles || !isAuthenticated()) return false;
    return forRoles.includes(authentication.profile!.role);
  };

  const isForProducts = (forProducts: Product[] | undefined) => {
    if (!forProducts || !isAuthenticated()) return false;
    const products = authentication.profile!.products;
    if (!products || products.length == 0) return false;
    return forProducts.every((a) => products.includes(a));
  };

  const isForProductsOnBuyersBehalf = (
    forProducts: Product[] | undefined,
    buyerCompanyId: string | undefined
  ) => {
    if (!forProducts || !isAuthenticated()) return false;
    const buyerProfile = resolveBuyerProfile(buyerCompanyId);

    return (
      buyerProfile &&
      buyerProfile.products &&
      buyerProfile.products.length > 0 &&
      forProducts.every((a) => buyerProfile.products.includes(a))
    );
  };

  return {
    getProfileId,
    isAuthenticated,
    hasAnyAuthority,
    hasAnyAuthorityOnBuyersBehalf,
    isForDepartments,
    isForDepartmentsOnBuyersBehalf,
    isForProductsOnBuyersBehalf,
    isForRole,
    isForProducts,
    isBuyer,
    isAgent,
    isVendor
  };
};
